import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { fmtMoney, fmtDate } from "../lib/format";
import { ChartBar, CaretRight, CaretDown } from "@phosphor-icons/react";

/**
 * v0.4.20.4 UI5 — Distance-sales threshold progress bar.
 *
 * Operator sign-off 2026-02-15:
 *   Q5-a = a (50/90 GREEN/AMBER/RED bands)
 *   Q5-b = a (cumulative this fiscal year)
 *
 * Headline: single progress bar + large total + status pill.
 * Audit detail (52-step list) collapsed by default under
 * `Show audit detail ▼`.
 */

const THRESHOLD_EUR = 10000;

const pctOf = (n, d) => {
  const r = parseFloat(n), t = parseFloat(d);
  if (!t) return 0;
  return Math.max(0, (r / t) * 100);
};

const statusFor = (pct, crossed) => {
  // A2-403: Art 59c — crossed only when EXCEEDED; exactly 100 % is not over.
  if (crossed || pct > 100) return { label: "CROSSED",  band: "red" };
  if (pct >= 90)             return { label: "AT RISK",  band: "red" };
  if (pct >= 50)             return { label: "MONITOR",  band: "amber" };
  return { label: "SAFE", band: "green" };
};

const BAND_COLOURS = {
  green: { bar: "bg-emerald-600",   text: "text-emerald-700 dark:text-emerald-300", chip: "bg-emerald-600/15 dark:bg-emerald-900/40 border-emerald-500/40 dark:border-emerald-700 text-emerald-700 dark:text-emerald-300" },
  amber: { bar: "bg-amber-500",     text: "text-amber-700 dark:text-amber-300",   chip: "bg-amber-50 dark:bg-amber-900/40 border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-300" },
  red:   { bar: "bg-rose-500",      text: "text-rose-700 dark:text-rose-300",    chip: "bg-rose-500/15 dark:bg-rose-900/40 border-rose-200 dark:border-rose-800 text-rose-700 dark:text-rose-300" },
};

export const ThresholdTimeline = ({ steps, applicable = true }) => {
  const { t } = useTranslation();
  const [auditOpen, setAuditOpen] = useState(false);

  // v0.5.9h — UK / non-EU-established sellers have NO €10,000 Union
  // distance-sales threshold (Non-Union OSS applies from the first
  // sale). Replace the misleading progress bar with a clarifying note.
  if (applicable === false) {
    return (
      <section
        data-testid="threshold-not-applicable"
        className="border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900"
      >
        <header className="border-b border-slate-200 dark:border-slate-700 px-4 py-2 flex items-center gap-2 text-xs tracking-normal text-slate-500 dark:text-slate-400">
          <ChartBar size={14} />
          {t("ui.threshold.title")}
        </header>
        <div
          data-testid="threshold-non-eu-note"
          className="px-4 py-3 text-[11px] text-slate-600 dark:text-slate-300"
        >
          {t("ui.threshold.non_eu_note")}
        </div>
      </section>
    );
  }

  if (!steps) return null;

  const empty = steps.length === 0;
  const threshold = empty ? THRESHOLD_EUR : parseFloat(steps[0].threshold_amount);
  const last = empty ? null : steps[steps.length - 1];
  const total = empty ? 0 : parseFloat(last.running_total);
  const crossed = !empty && steps.some((s) => s.crossed_threshold);
  const pct = Math.min(100, pctOf(total, threshold));
  const status = statusFor(pct, crossed);
  const colours = BAND_COLOURS[status.band];

  return (
    <section
      data-testid="threshold-timeline"
      data-band={status.band}
      className="border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900"
    >
      <header className="border-b border-slate-200 dark:border-slate-700 px-4 py-2 flex items-center justify-between">
        <div className="text-xs tracking-normal text-slate-500 dark:text-slate-400 flex items-center gap-2">
          <ChartBar size={14} />
          {t("ui.threshold.title")}
        </div>
        <span
          data-testid="threshold-status-pill"
          className={`text-[10px] tracking-normal px-2 py-0.5 rounded-full border ${colours.chip}`}
        >
          {status.label}
        </span>
      </header>

      {/* Q5-a — single progress bar with band-coloured fill */}
      <div className="px-4 pt-4 pb-2">
        <div className="flex items-baseline gap-3 mb-2">
          <span data-testid="threshold-total" className={`tabular-nums text-3xl font-black ${colours.text}`}>
            €{fmtMoney(total)}
          </span>
          <span className="text-xs text-slate-600 dark:text-slate-300">
            {t("ui.threshold.of_threshold", { threshold: fmtMoney(threshold) })}
          </span>
          <span data-testid="threshold-pct" className="tabular-nums text-xs text-slate-500 dark:text-slate-400 ml-auto">
            {pct.toFixed(1)}%
          </span>
        </div>
        {/* Progress track */}
        <div
          data-testid="threshold-progress-bar"
          role="progressbar"
          aria-valuenow={Math.round(pct)}
          aria-valuemin={0}
          aria-valuemax={100}
          className="relative w-full h-3 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-full overflow-hidden"
        >
          <div className={`absolute inset-y-0 left-0 ${colours.bar} transition-[width] duration-300`} style={{ width: `${pct}%` }} />
          {/* 50% + 90% band markers */}
          <div className="absolute inset-y-0 border-l border-amber-200 dark:border-amber-800" style={{ left: "50%" }} title={t("ui.threshold.band_monitor")} />
          <div className="absolute inset-y-0 border-l border-rose-200 dark:border-rose-800"  style={{ left: "90%" }} title={t("ui.threshold.band_at_risk")} />
        </div>
        <div className="flex justify-between mt-1 tabular-nums text-[10px] text-slate-600 dark:text-slate-300">
          <span>€0</span>
          <span className="text-amber-600/70">50%</span>
          <span className="text-rose-500/70 dark:text-rose-400/70">90%</span>
          <span>€{fmtMoney(threshold)}</span>
        </div>
        {empty ? (
          <div className="mt-3 text-[11px] text-slate-500 dark:text-slate-400">
            {t("ui.threshold.empty")}
          </div>
        ) : (
          <div className="mt-3 text-[11px] text-slate-600 dark:text-slate-300">
            {crossed
              ? t("ui.threshold.crossed_hint")
              : t("ui.threshold.safe_hint")}
          </div>
        )}
      </div>

      {/* Audit detail — collapsed by default */}
      {!empty && (
        <div className="border-t border-slate-200 dark:border-slate-700">
          <button
            type="button"
            data-testid="threshold-audit-toggle"
            onClick={() => setAuditOpen(!auditOpen)}
            className="w-full flex items-center justify-between px-4 py-2 text-left hover:bg-white dark:bg-slate-900"
          >
            <span className="text-[10px] tracking-normal text-slate-500 dark:text-slate-400 flex items-center gap-2">
              {auditOpen ? <CaretDown size={12} /> : <CaretRight size={12} />}
              {t("ui.threshold.audit_detail", { count: steps.length })}
            </span>
            <span className="text-[10px] text-slate-600 dark:text-slate-300">
              {auditOpen ? t("ui.threshold.hide") : t("ui.threshold.show")}
            </span>
          </button>
          {auditOpen && (
            <div data-testid="threshold-audit-body" className="p-4 space-y-1 border-t border-slate-200 dark:border-slate-700">
              {steps.map((s, i) => {
                const w = Math.min(100, pctOf(s.running_total, threshold));
                return (
                  <div key={i} data-testid={`threshold-step-${i}`} className="grid grid-cols-12 gap-2 tabular-nums text-xs items-center">
                    <span className="col-span-2 text-slate-600 dark:text-slate-300">{fmtDate(s.transaction_date)}</span>
                    <span className="col-span-1 text-teal-700 dark:text-teal-300">{s.buyer_country}</span>
                    <div className="col-span-6 relative h-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded">
                      <div className={s.crossed_threshold ? "h-full bg-rose-500" : "h-full bg-teal-600/60"} style={{ width: `${w}%` }} />
                    </div>
                    <span className="col-span-2 text-right text-slate-500 dark:text-slate-400">+€{fmtMoney(s.incremental_amount)}</span>
                    <span className={`col-span-1 text-right ${s.crossed_threshold ? "text-rose-500 dark:text-rose-500 dark:text-rose-400 font-bold" : "text-slate-900 dark:text-slate-50"}`}>
                      €{fmtMoney(s.running_total)}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </section>
  );
};
